import { ArrowRight, ClipboardCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { ProjectTimeIndicator } from '@/features/dashboard/components/dashboard/project-time-indicator';
import { KpiWithLatestEntry } from '@/features/dashboard/hooks/use-kpis-with-latest-entries';
import { Gradients } from '@/shared/components/theme/gradient';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import { getInitials } from '@/shared/utils/avatar-util';
import { formatDate } from '@/shared/utils/format';

export const KpiLatestEntryCard = ({ kpi }: { kpi: KpiWithLatestEntry }) => {
  const { t: tCategories } = useTranslation('categories', {
    keyPrefix: 'Level1',
  });
  const { t } = useTranslation('overview', {
    keyPrefix: 'OverviewPage.kpiCards',
  });
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(
      paths.projects.details.base.getHref(kpi.project.id.toString()) +
        `?kpiId=${kpi.id}`,
    );
  };

  const formattedDate = kpi.lastEntryDate
    ? formatDate({
        date: kpi.lastEntryDate,
        formatType: 'digits',
      })
    : null;

  const lastTrackerOrg = kpi.lastTrackerOrg;
  const orgName = lastTrackerOrg?.name || '-';

  return (
    <div
      className="flex h-[345px] cursor-pointer flex-col gap-6 overflow-hidden rounded-[24px] bg-light-container p-4"
      onClick={handleCardClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex size-[38px] shrink-0 items-center justify-center rounded-full ${Gradients.purpleBlueGradient}`}
          >
            <ClipboardCheck className="size-5 text-white" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-semibold text-white">{kpi.project.name}</p>
            <p className="text-3xs text-muted-foreground">
              {tCategories(kpi.project.category as any) || '-'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          btnType="icon"
          shape="square"
          className="h-9 w-9 shrink-0 border border-white/50"
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
        >
          <ArrowRight className="h-4 w-4 text-white" />
        </Button>
      </div>
      <div className="flex w-full flex-1 flex-row gap-3">
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex flex-1 flex-col items-center justify-between rounded-md bg-primary-shade-grey-500/5 px-6 py-3.5">
            <p className="text-3xs text-primary-shade-grey-500">
              {t('lastTrackedBy')}
            </p>
            <div className="flex flex-col items-center justify-center gap-2">
              {lastTrackerOrg ? (
                <>
                  <Avatar variant="outlined" className="size-[32px]">
                    <AvatarImage
                      src={lastTrackerOrg.image_url || ''}
                      alt={orgName}
                    />
                    <AvatarFallback>{getInitials(orgName)}</AvatarFallback>
                  </Avatar>
                  <p className="max-w-full truncate text-2xs font-medium text-white">
                    {orgName}
                  </p>
                </>
              ) : (
                <p className="text-2xs text-primary-shade-grey-500">-</p>
              )}
            </div>
          </div>

          <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-md bg-primary-shade-grey-500/5 px-6 py-5">
            <p className="text-3xs text-primary-shade-grey-500">
              {t('lastEntry')}
            </p>
            <p className="font-semibold text-white">{formattedDate}</p>
          </div>
        </div>
        {kpi.project.start_date && kpi.project.end_date && (
          <ProjectTimeIndicator
            startDate={kpi.project.start_date}
            endDate={kpi.project.end_date}
          />
        )}
      </div>
    </div>
  );
};
